/**
 * PATH: apps/qa/src/pages/signoffs-page/SignaturePad.tsx
 * PURPOSE: Canvas-based signature capture component
 */

import { useState, useRef } from 'react'
import { Trash2, Pen } from 'lucide-react'

interface SignaturePadProps {
  onSignatureCapture: (dataUrl: string) => void
  onClear: () => void
}

export function SignaturePad({ onSignatureCapture, onClear }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasSignature, setHasSignature] = useState(false)

  const getContext = () => {
    const canvas = canvasRef.current
    if (!canvas) return null
    const ctx = canvas.getContext('2d')
    if (ctx) { ctx.strokeStyle = '#1a1a1a'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round' }
    return ctx
  }

  const getCoords = (e: React.MouseEvent | React.TouchEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect()
    if ('touches' in e) return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    const ctx = getContext()
    if (!ctx) return
    setIsDrawing(true); setHasSignature(true)
    const { x, y } = getCoords(e)
    ctx.beginPath(); ctx.moveTo(x, y)
  }

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return
    const ctx = getContext()
    if (!ctx) return
    const { x, y } = getCoords(e)
    ctx.lineTo(x, y); ctx.stroke()
  }

  const stopDrawing = () => {
    if (isDrawing && hasSignature && canvasRef.current) onSignatureCapture(canvasRef.current.toDataURL('image/png'))
    setIsDrawing(false)
  }

  const clearSignature = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height)
    setHasSignature(false); onClear()
  }

  return (
    <div className="space-y-2">
      <div className="relative border-2 border-dashed border-border rounded-xl overflow-hidden bg-white dark:bg-slate-900">
        <canvas ref={canvasRef} width={400} height={150} className="touch-none w-full cursor-crosshair"
          onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
          onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-muted-foreground text-sm flex items-center gap-2"><Pen className="w-4 h-4" />Sign here</span>
          </div>
        )}
      </div>
      {hasSignature && (
        <button onClick={clearSignature} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-destructive transition-colors">
          <Trash2 className="w-3.5 h-3.5" /> Clear signature
        </button>
      )}
    </div>
  )
}
